/**
 * AnnotationPopover — Phase 2 (Feb 2026).
 *
 * Floating popover that appears above a text selection inside a
 * clause-tagged contract bubble. Three actions only:
 *
 *   Comment   |   Save for later   |   Assign
 *
 * Resolve is intentionally NOT here — it lives on existing
 * annotations in the activity panel.
 *
 * Selection lifecycle:
 *   1. Container listens on `mouseup` and `selectionchange`.
 *   2. If the selection is collapsed → hide.
 *   3. If the selection has a `data-clause-id` ancestor → show.
 *   4. Position is computed from `range.getBoundingClientRect()`.
 *      RTL contracts: anchor flips to the selection's right edge.
 *
 * Required props from the host:
 *   • documentId  (= message.meta.draft_id)
 *   • matterId    (= currentChatId)
 *   • accessToken
 *
 * Styling: monochrome — #0D0D0D surface, #1A1A1A border, white text.
 * Icons: lucide-react MessageSquare, Bookmark, UserPlus. No emoji.
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, Bookmark, UserPlus, X, Loader2 } from 'lucide-react';
import { createAnnotation } from './annotationsApi';

const POPOVER_OFFSET = 10; // px above/below selection
const POPOVER_WIDTH = 340;

const isRTL = () => {
  if (typeof document === 'undefined') return false;
  return (
    document.dir === 'rtl' ||
    document.documentElement?.dir === 'rtl' ||
    document.body?.dir === 'rtl'
  );
};

const findClauseAnchor = (node) => {
  let el = node;
  while (el && el.nodeType !== 1) el = el.parentNode;
  while (el && el !== document.body) {
    if (el.dataset && el.dataset.clauseId) {
      return {
        clauseId: el.dataset.clauseId,
        header: el.dataset.clauseHeader || '',
      };
    }
    el = el.parentElement;
  }
  return null;
};

const positionFor = (rect, rtl) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Anchor X: by default centered on selection; clamp into viewport.
  let x = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
  if (rtl) {
    // RTL flip: anchor to the right edge of the selection.
    x = rect.right - POPOVER_WIDTH;
  }
  x = Math.max(12, Math.min(vw - POPOVER_WIDTH - 12, x));
  // Anchor Y: above the selection if there's room, else below.
  let y = rect.top - POPOVER_OFFSET - 56;
  let placement = 'top';
  if (y < 12) {
    y = rect.bottom + POPOVER_OFFSET;
    placement = 'bottom';
  }
  y = Math.max(12, Math.min(vh - 80, y));
  return { x, y, placement };
};

export const AnnotationPopover = ({
  documentId,
  matterId,
  accessToken,
  onCreated,
}) => {
  const [state, setState] = useState({
    visible: false, x: 0, y: 0, placement: 'top',
    quote: '', clauseId: null, header: '',
  });
  const [mode, setMode] = useState(null); // null | 'comment' | 'assign'
  const [body, setBody] = useState('');
  const [assignee, setAssignee] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const popoverRef = useRef(null);
  const rtl = isRTL();

  const reset = useCallback(() => {
    setState((s) => ({ ...s, visible: false }));
    setMode(null);
    setBody('');
    setAssignee('');
    setError('');
    setSubmitting(false);
  }, []);

  // Track selection changes (mouseup is the lowest-latency signal).
  useEffect(() => {
    const handleSelection = () => {
      const sel = window.getSelection?.();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      const range = sel.getRangeAt(0);
      // Anchor search: prefer the range's startContainer; if that's a
      // non-Text wrapper (e.g. <p> inside a markdown-rendered clause span),
      // try the commonAncestorContainer too so programmatic Range builds
      // and zero-width selections that still hit a clause still mount
      // the popover (iter72 RCA). Quote may end up empty; the bookmark
      // path tolerates that.
      const anchor = (
        findClauseAnchor(range.startContainer)
        || findClauseAnchor(range.commonAncestorContainer)
      );
      if (!anchor) return; // not inside a clause-tagged contract
      const rect = range.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) return;
      const { x, y, placement } = positionFor(rect, isRTL());
      setState({
        visible: true, x, y, placement,
        quote: text, clauseId: anchor.clauseId, header: anchor.header,
      });
      setMode(null);
      setBody('');
      setAssignee('');
      setError('');
    };
    const handleMouseUp = (e) => {
      // Ignore clicks inside the popover itself.
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      setTimeout(handleSelection, 0);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') reset();
    };
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleEscape);
    // Iter73 follow-up (Suite 2 P6): when the source range scrolls
    // out from under the popover, dismiss the popover instead of
    // leaving it absolutely positioned in mid-air. Capture phase so
    // we catch scroll on any ancestor container, not just the window.
    const handleScroll = () => {
      // Use rAF so a rapid scroll burst dismisses just once.
      window.requestAnimationFrame(() => {
        setState((s) => (s.visible ? { ...s, visible: false } : s));
      });
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [reset]);

  const submit = useCallback(async (type) => {
    if (!documentId || !matterId || !state.clauseId) {
      setError('Missing draft context — try selecting again.');
      return;
    }
    if (type === 'comment' && !body.trim()) {
      setError('Comment cannot be empty.');
      return;
    }
    if (type === 'assign' && !assignee.trim()) {
      setError('Pick a teammate to assign.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        clause_id: state.clauseId,
        quote: state.quote,
        type: type === 'bookmark' ? 'bookmark' : 'comment',
        body: type === 'bookmark' ? 'Saved for later' : body.trim(),
        document_id: documentId,
        matter_id: matterId,
        ...(type === 'assign' ? { assignee_id: assignee.trim() } : {}),
      };
      const created = await createAnnotation({ accessToken, payload });
      window.dispatchEvent(new CustomEvent('bayenah:annotation-created', {
        detail: { documentId, annotation: created },
      }));
      if (typeof onCreated === 'function') onCreated(created);
      reset();
      // Drop the browser selection so the popover doesn't bounce back.
      window.getSelection()?.removeAllRanges();
    } catch (err) {
      setError(err?.message || 'Failed to save annotation.');
    } finally {
      setSubmitting(false);
    }
  }, [accessToken, assignee, body, documentId, matterId, onCreated, reset, state.clauseId, state.quote]);

  if (!state.visible) return null;

  const surface = '#0D0D0D';
  const border = '#1A1A1A';
  const muted = 'rgba(255,255,255,0.55)';

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Annotation actions"
      data-testid="annotation-popover"
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        position: 'fixed',
        left: state.x,
        top: state.y,
        width: POPOVER_WIDTH,
        zIndex: 9999,
        background: surface,
        border: `1px solid ${border}`,
        color: '#fff',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.55)',
        backdropFilter: 'blur(10px)',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2"
        style={{ borderBottom: `1px solid ${border}` }}
      >
        <div
          className="text-[11px] uppercase tracking-wider truncate"
          style={{ color: muted }}
          data-testid="annotation-popover-header"
        >
          {state.header
            ? `${state.clauseId} · ${state.header}`
            : state.clauseId}
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={reset}
          className="text-white/40 hover:text-white"
          data-testid="annotation-popover-close"
        >
          <X size={14} />
        </button>
      </div>

      {/* Action row (always visible) */}
      {!mode && (
        <div className="grid grid-cols-3 gap-1 p-2">
          <button
            type="button"
            onClick={() => setMode('comment')}
            className="flex flex-col items-center justify-center gap-1 rounded-md py-2 hover:bg-white/[0.06] transition-colors"
            data-testid="annotation-action-comment"
          >
            <MessageSquare size={16} />
            <span className="text-xs">Comment</span>
          </button>
          <button
            type="button"
            onClick={() => submit('bookmark')}
            disabled={submitting}
            className="flex flex-col items-center justify-center gap-1 rounded-md py-2 hover:bg-white/[0.06] transition-colors disabled:opacity-50"
            data-testid="annotation-action-bookmark"
          >
            <Bookmark size={16} />
            <span className="text-xs">Save for later</span>
          </button>
          <button
            type="button"
            onClick={() => setMode('assign')}
            className="flex flex-col items-center justify-center gap-1 rounded-md py-2 hover:bg-white/[0.06] transition-colors"
            data-testid="annotation-action-assign"
          >
            <UserPlus size={16} />
            <span className="text-xs">Assign</span>
          </button>
        </div>
      )}

      {/* Comment form */}
      {mode === 'comment' && (
        <div className="p-3 space-y-2">
          <textarea
            autoFocus
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write a comment…"
            data-testid="annotation-comment-input"
            className="w-full rounded-md px-2 py-1.5 text-sm bg-black/40 border outline-none focus:border-white/30"
            style={{ borderColor: border, color: '#fff' }}
          />
          {error ? (
            <div className="text-xs text-red-400" data-testid="annotation-error">{error}</div>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={reset}
              className="text-xs px-3 py-1.5 rounded-md border hover:bg-white/[0.04]"
              style={{ borderColor: border, color: muted }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => submit('comment')}
              disabled={submitting}
              data-testid="annotation-comment-submit"
              className="text-xs px-3 py-1.5 rounded-md bg-white text-black font-medium hover:bg-white/90 disabled:opacity-50 flex items-center gap-1"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
              Save
            </button>
          </div>
        </div>
      )}

      {/* Assign form */}
      {mode === 'assign' && (
        <div className="p-3 space-y-2">
          <input
            autoFocus
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            placeholder="Assignee user id"
            data-testid="annotation-assign-input"
            className="w-full rounded-md px-2 py-1.5 text-sm bg-black/40 border outline-none focus:border-white/30"
            style={{ borderColor: border, color: '#fff' }}
          />
          <textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a note (optional)…"
            data-testid="annotation-assign-note"
            className="w-full rounded-md px-2 py-1.5 text-sm bg-black/40 border outline-none focus:border-white/30"
            style={{ borderColor: border, color: '#fff' }}
          />
          {error ? (
            <div className="text-xs text-red-400" data-testid="annotation-error">{error}</div>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={reset}
              className="text-xs px-3 py-1.5 rounded-md border hover:bg-white/[0.04]"
              style={{ borderColor: border, color: muted }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => submit('assign')}
              disabled={submitting}
              data-testid="annotation-assign-submit"
              className="text-xs px-3 py-1.5 rounded-md bg-white text-black font-medium hover:bg-white/90 disabled:opacity-50 flex items-center gap-1"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
              Assign
            </button>
          </div>
        </div>
      )}

      {/* Quote preview */}
      <div
        className="px-3 py-2 text-[11px]"
        style={{ borderTop: `1px solid ${border}`, color: muted }}
      >
        <span className="opacity-70">{rtl ? '"' : '"'}</span>
        <span className="line-clamp-2">{state.quote}</span>
        <span className="opacity-70">{rtl ? '"' : '"'}</span>
      </div>
    </div>,
    document.body
  );
};

export default AnnotationPopover;
